import React, { useCallback, useEffect, useLayoutEffect } from 'react'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { BtCheckBox, BtText } from '@linqpal/components/src/ui'
import { Input } from '../../../ui/atoms/builder-2.0/Input'
import BackButton from '../BackButton'
import { observer } from 'mobx-react'
import SubmitButton from '../SubmitButton'
import Spacer from '../../../ui/atoms/Spacer'
import Layout from '../Layout'
import Title, { SubTitle } from '../Title'
import SignupStore from '../stores/SignupStore'
import RootStore from '../../../store'
import { paths } from '../../links'
import { useTranslation } from 'react-i18next'
import { useResponsive } from '../../../utils/hooks'
import { AlreadyHaveAcc } from '../AlreadyHaveAcc'
import { ErrorMessage } from '../ErrorMessage'
import Loading from '../../Loading'
import { invitationTypes } from '@linqpal/models/src/dictionaries'
import PhoneNumberInput from '../../../ui/organisms/PhoneNumberInput'

interface SignUpFormProps {
  navigation: any
  onBack?: () => void
  dontNavigate?: boolean
}

export default observer(
  ({ navigation, onBack, dontNavigate }: SignUpFormProps) => {
    const {
      firstName,
      lastName,
      setValue,
      type,
      email,
      phone,
      canSignUp,
      canSubmit,
      isValidLogin,
      isValidEmail,
      signUp,
      errors,
      referalCode,
      company_name,
      setIsBusiness,
      isBusiness,
      signupInvoiceId,
      isInvitationOrInvoiceLinkSignup,
      invitationType,
      isInvitation,
      isWithinCompanyInvitation,
    } = SignupStore

    const { isBusy } = RootStore
    const { t } = useTranslation('auth')
    const { sm } = useResponsive()
    const pressContinue = useCallback(async () => {
      signUp(signupInvoiceId)
    }, [signUp, signupInvoiceId])

    useEffect(() => SignupStore.removeError('signup'), [])

    useEffect(() => {
      if (canSignUp && isInvitation && isWithinCompanyInvitation) {
        signUp().then(() => {
          if (invitationType === invitationTypes.GETPAID_INVITE)
            navigation.replace(paths.GetPaidApplication)
          else if (invitationType === invitationTypes.CREDIT_INVITE)
            navigation.navigate(paths.CreditApplication)
          RootStore.initRouteParams({})
        })
      }
    }, [
      canSignUp,
      isInvitation,
      invitationType,
      signUp,
      isWithinCompanyInvitation,
      navigation,
    ])

    useLayoutEffect(() => {
      if (!isValidLogin) {
        navigation.replace(paths.Auth.SignUp)
      } else if (!canSubmit) {
        navigation.replace(
          type === 'password' ? paths.Auth.SignUpPassword : paths.Auth.SignUp,
        )
      }
    }, [canSubmit, isValidEmail, isValidLogin, navigation, type])

    const onBackPress = () => (onBack ? onBack() : navigation.goBack())

    if (
      canSignUp &&
      isInvitation &&
      isWithinCompanyInvitation &&
      !errors.get('signup')?.message
    )
      return <Loading />

    return (
      <Layout
        onBackPress={onBackPress}
        contentStyle={{ marginTop: sm ? 0 : 20 }}
      >
        <BackButton onPress={onBackPress} testID="signupFormBack" />
        <Title style={{ alignSelf: undefined }}>{t('signUp.formTitle')}</Title>
        <Spacer height={16} />
        <SubTitle style={{ alignSelf: undefined }}>
          {t('signUp.formSubTitle')}
        </SubTitle>

        <View style={{ flexDirection: sm ? 'row' : 'column' }}>
          <View style={{ flex: 1 }}>
            <Input
              testID="firstName"
              value={firstName}
              onChangeText={(value: string) => setValue('firstName', value)}
              label={t('signUp.firstNameLabel')}
              mandatory
              autoFocus
            />
          </View>
          {sm && <Spacer width={20} />}
          <View style={{ flex: 1 }}>
            <Input
              testID="lastName"
              value={lastName}
              onChangeText={(value: string) => setValue('lastName', value)}
              label={t('signUp.lastNameLabel')}
              mandatory
            />
          </View>
        </View>

        {type === 'code' ? (
          <Input
            testID="emailPhone"
            value={email}
            onChangeText={(value: string) => setValue('email', value)}
            label={t('signUp.loginLabelEmail')}
            mandatory
          />
        ) : (
          <PhoneNumberInput
            testID="phone"
            label={t('signUp.loginLabelPhone')}
            value={phone}
            onChangeText={(text) => {
              setValue('phone', text)
            }}
            mandatory
          />
        )}
        <ErrorMessage error={errors.get('login')?.message} />
        <Input
          testID="businessNameInput"
          value={company_name}
          label={t('businessName')}
          onChangeText={(value) => setValue('company_name', value)}
          disabled={!isBusiness}
          mandatory
        />
        {isInvitationOrInvoiceLinkSignup && (
          <TouchableOpacity
            style={styles.checkboxWrapper}
            onPress={setIsBusiness}
            activeOpacity={0.7}
          >
            <BtCheckBox
              style={styles.checkbox}
              checked={!isBusiness}
              onPress={setIsBusiness}
            />
            <BtText style={styles.checkboxLabel}>
              {t('signUp.ImNotABusiness')}
            </BtText>
          </TouchableOpacity>
        )}
        <Input
          testID="referalInput"
          value={referalCode}
          label={t('referalCode')}
          onChangeText={(value) => setValue('referalCode', value)}
        />
        {errors.get('signup')?.message ? (
          <>
            <Spacer height={30} />
            <ErrorMessage error={errors.get('signup')?.message} />
            <Spacer height={10} />
          </>
        ) : (
          <Spacer height={45} />
        )}
        <SubmitButton
          onPress={pressContinue}
          disabled={!canSignUp || isBusy}
          loading={isBusy}
          testID="continue-button"
        >
          {t('signUp.formSubmitLabel')}
        </SubmitButton>
        <Spacer height={40} />
        <AlreadyHaveAcc
          navigation={navigation}
          testID="login-link"
          dontNavigate={dontNavigate}
        />
      </Layout>
    )
  },
)

const styles = StyleSheet.create({
  checkboxWrapper: { flexDirection: 'row', marginTop: 16 },
  checkbox: { width: 20, height: 20, marginRight: 11 },
  checkboxLabel: {
    fontWeight: '400',
    fontSize: 14,
    lineHeight: 20,
    color: '#668598',
  },
})
